import { defineStore } from "pinia";

import type { ClinicalHistoryState } from "./Models/State";
import type {
  PatientInfoUpdateQuery,
  Session,
  SessionUpdateQuery,
  SessionRegisterQuery,
} from "../Models/Domain/ClinicalHistory";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import getPatientAH from "./ActionHandlers/GetPatient";
import getSessionsAH from "./ActionHandlers/GetSessions";
import getSessionDownloadLinkAH from "./ActionHandlers/GetSessionDownloadLink";
import getNotesAH from "./ActionHandlers/GetNotes";
import updateNotesAH from "./ActionHandlers/UpdateNotes";
import updatePatientInfoAH from "./ActionHandlers/UpdatePatientInfo";
import updateSessionAH from "./ActionHandlers/UpdateSession";
import registerSessionAH from "./ActionHandlers/RegisterSession";
import getSessionAH from "./ActionHandlers/GetSession";

import { hasValue } from "@/Utils/Objects";

const ClinicalHistoryState: ClinicalHistoryState = {
  patientInfo: null,
  sessionSummary: [],
  notes: null,
  sessionSelectedLink: null,
  session: null,
  nextSessionAvailable: null,
  isLoadingPatientInfo: true,
  isLoadingSessions: true,
  isLoadingNotes: true,
  isLoadingSession: false,
  isTranscriptionEnabled: false,
};

export const useClinicalHistoryStore = defineStore(StateNamespace.CLINICAL_HISTORY, {
  state: () => ({ ...ClinicalHistoryState }),

  getters: {},

  actions: {
    fetchPatientInfo(uuid?: string) {
      return getPatientAH(uuid);
    },

    fetchSessions(customerId?: string) {
      return getSessionsAH(customerId);
    },

    fetchSession(uuid: string, customerId?: string): Promise<Session | undefined> {
      return getSessionAH(uuid, customerId);
    },

    getSessionDownloadLink() {
      if (!hasValue(this.session)) return;

      return getSessionDownloadLinkAH(this.session.uuid);
    },

    fetchNotes() {
      return getNotesAH();
    },

    updateNotes(notesData: string) {
      if (!hasValue(this.notes)) return;

      return updateNotesAH(this.notes.uuid, notesData);
    },

    updatePatientInfo(patientInfoData: Partial<PatientInfoUpdateQuery>) {
      if (!hasValue(this.patientInfo)) return;

      return updatePatientInfoAH(this.patientInfo.uuid, patientInfoData);
    },

    registerSession(sessionData: SessionRegisterQuery) {
      return registerSessionAH(sessionData);
    },

    updateSession(sessionData: SessionUpdateQuery) {
      if (!hasValue(this.session)) return;

      return updateSessionAH(this.session.uuid, sessionData);
    },

    selectSession(session: Session | null) {
      this.session = session;
    },
  },
});

export type ClinicalHistoryStore = ReturnType<typeof useClinicalHistoryStore>;

export default useClinicalHistoryStore;
